import {Popover, Tag} from 'antd';
import classNames from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useMemo, useRef} from 'react';

import {Tooltip} from '@/components/Tooltip';

import style from './index.less';

const truncateText = (text, isOnPopover = false, maxLength = 45) => {
  if (isOnPopover) maxLength = maxLength - 5;
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '...';
};

const mapFunc = (item, key, renderField, conditionalColor, tag, primary, customClass, isOnPopover) => {
  const text = renderField ? get(item, renderField) : item;
  const color = conditionalColor ? conditionalColor(item) : undefined;
  if (tag)
    return text ? (<Tooltip title={text} key={key}>
      <Tag
        color={color}
        className={classNames(
          {'mb-1': isOnPopover},
          'table-tag-item flex px-2 py-1 bg-white truncate mx-0',
          {'bg-primary text-white': primary}, customClass)}>
        <div className={'flex items-center'}>
          <span className={'flex-grow'}>{truncateText(text, isOnPopover)}</span>
        </div>
      </Tag>
    </Tooltip>) : '';
  return <Tooltip title={text} key={key}>
    <div key={key}
      className={classNames('mr-2 truncate',
        {'text-primary': primary}, {'w-full': isOnPopover}, customClass)}>{text}</div>
  </Tooltip>;
};

const TagList = ({
  value, renderField, tag, limit, conditionalColor, primary, customClass, containerCustomClass,
  customMapFunc, defaultValue, notUsePopupContainer = false, disableOthers = false
}) => {
  const ref = useRef();
  const toShow = useMemo(() => limit ? (value ? [...value]?.splice(0, limit) : []) : value, [value, limit]);
  const restToShow = useMemo(() => limit ? (value ? [...value]?.splice(limit, value?.length || 0) : []) : [], [value, limit]);
  
  const getPopupContainer = useCallback(() => {
    return ref.current;
  }, []);
  
  if ((!value || !value.length) && !defaultValue) return '';
  if ((!value || !value.length) && defaultValue) return mapFunc(defaultValue, 0, renderField, conditionalColor, tag, primary, customClass);
  
  const tags = toShow?.map(customMapFunc)?.map((item, key) => mapFunc(item, key, renderField, conditionalColor, tag, primary, customClass));
  const tagsOther = restToShow?.map(customMapFunc)?.map((item, key) => mapFunc(item, key, renderField, conditionalColor, tag, primary, customClass, true));
  
  return <div ref={ref} id={'tag-list-elements'}
    className={classNames('flex items-center', style.restPopoverClass, containerCustomClass)}>
    {tags}
    {!!restToShow.length &&
      <Popover
        disabled={disableOthers}
        className={'cursor-pointer'}
        placement="bottomRight"
        trigger="click"
        {...({getPopupContainer: notUsePopupContainer ? null : getPopupContainer})}
        content={<div className="w-full margin-auto" style={{maxWidth: 320}}>
          {tagsOther}
        </div>}>
        <Tag
          key={'more-tags'}
          className={classNames('font-medium px-2 py-1 bg-white ml-1', {'bg-primary text-white': primary}, customClass)}
          closable={false}
        >
          +{restToShow.length}
        </Tag>
      </Popover>}
  </div>;
};

export default memo(TagList);

TagList.propTypes = {
  conditionalColor: PropTypes.func,
  value: PropTypes.array,
  primary: PropTypes.bool,
  disableOthers: PropTypes.bool,
  tag: PropTypes.bool,
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  renderField: PropTypes.string,
  defaultValue: PropTypes.string,
  containerCustomClass: PropTypes.string,
  customMapFunc: PropTypes.func,
  customClass: PropTypes.string,
  notUsePopupContainer: PropTypes.bool
};

TagList.defaultProps = {
  customMapFunc: (v) => v,
  value: [],
};
